/**
 * Empty state component for when there's no data
 *
 * @example
 * ```tsx
 * <EmptyState
 *   title="No items found"
 *   message="Try adjusting your filters"
 * />
 * ```
 */

"use client";

import { Box, Flex } from "@chakra-ui/react";
import type React from "react";
import { useThemeTokens } from "@/lib/theme/tokens";
import { Card, Heading, Link, Text } from "../ui";

export interface EmptyStateProps {
  /**
   * Optional icon (e.g. from react-icons) shown above title in primary color
   */
  icon?: React.ReactNode;
  /**
   * Title text
   */
  title?: string;
  /**
   * Message text (can be string or ReactNode)
   */
  message?: string | React.ReactNode;
  /**
   * Contact email to show
   */
  contactEmail?: string;
  /**
   * Whether to show contact information
   */
  showContact?: boolean;
  /**
   * Compact layout (e.g. inside dashboard widgets) – no min height, less padding
   */
  compact?: boolean;
  /**
   * Fill remaining space inside a flex parent (e.g. widget body below a fixed header).
   */
  fillParent?: boolean;
}

export function EmptyState({
  icon,
  title = "No data available",
  message = "There are currently no items to display.",
  contactEmail,
  showContact = false,
  compact = false,
  fillParent = false,
}: EmptyStateProps) {
  const theme = useThemeTokens();
  const defaultIconSize = compact ? 32 : 40;

  const iconNode = icon ? (
    compact ? (
      <Box
        p={3}
        borderRadius="12px"
        bg={theme.accent.primaryBg}
        color={theme.accent.primary}
        display="flex"
        alignItems="center"
        justifyContent="center"
        lineHeight={0}
      >
        <Box
          sx={{
            "& svg": {
              width: `${defaultIconSize}px`,
              height: `${defaultIconSize}px`,
            },
          }}
        >
          {icon}
        </Box>
      </Box>
    ) : (
      <Box
        color={theme.accent.primary}
        mb={0}
        sx={{
          "& svg": {
            width: `${defaultIconSize}px`,
            height: `${defaultIconSize}px`,
          },
        }}
      >
        {icon}
      </Box>
    )
  ) : null;

  return (
    <Card
      p={compact ? (fillParent ? 12 : 4) : 12}
      textAlign="center"
      borderWidth="1px"
      borderStyle="dashed"
      borderColor={theme.borderGlass.default}
      bg={compact ? "transparent" : undefined}
      flex={fillParent ? 1 : undefined}
      minH={fillParent ? 0 : compact ? undefined : "60vh"}
      minW={fillParent ? 0 : undefined}
      w={fillParent ? "100%" : undefined}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Flex direction="column" align="center" gap={compact ? 3 : 3}>
        {iconNode}
        {compact ? (
          <Text fontSize="sm" fontWeight="600" color={theme.text.secondary}>
            {title}
          </Text>
        ) : (
          <Heading size="lg" mb={2}>
            {title}
          </Heading>
        )}
        {typeof message === "string" ? (
          <Text
            variant="secondary"
            fontSize={compact ? "xs" : "md"}
            maxW={compact ? "400px" : "600px"}
            color={theme.text.tertiary}
          >
            {message}
            {showContact && contactEmail && (
              <>
                {" "}
                Contact us at{" "}
                <Link
                  href={`mailto:${contactEmail}`}
                  color={theme.accent.primary}
                >
                  {contactEmail}
                </Link>
                .
              </>
            )}
          </Text>
        ) : (
          message ? (
          <Box maxW={compact ? "400px" : "600px"} w="100%">
            {message}
            {showContact && contactEmail && (
              <Text variant="secondary" fontSize="md" mt={2}>
                Contact us at{" "}
                <Link
                  href={`mailto:${contactEmail}`}
                  color={theme.accent.primary}
                >
                  {contactEmail}
                </Link>
                .
              </Text>
            )}
          </Box>
          ) : null
        )}
      </Flex>
    </Card>
  );
}
